import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Box,
  Button,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import {
  AddOutlined,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  SearchOutlined,
  Settings as SettingsIcon,
  DeleteSweepOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import { ChangeMachine, CustomerMachineryState, Machine, MachinesQuery } from "../models/models";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import ActionToast from "../../../shared/components/ActionToast";
import Loader from "../../../shared/components/Loader";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import {
  changeQuery,
  createMachine,
  decommissionMachine,
  getDecommissionedMachines,
  getMachines,
  getTypes,
  restoreMachine,
  selectMachine,
  setError,
  setSuccess,
} from "../customerMachinerySlice";
import { fetchGroups } from "../machineGroupsSlice";
import MachineDialog from "./MachineDialog";
import MachineEditDialog from "./MachineEditDialog";
import MachineQRCodeDialog from "./MachineQRCodeDialog";
/* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
// import MachineWhatsAppQRDialog from "./MachineWhatsAppQRDialog";
import GroupTogglePills from "./GroupTogglePills";
import ManageGroupsDialog from "./ManageGroupsDialog";
import AssignGroupDialog from "./AssignGroupDialog";
import { NotificationMessage } from "../../../shared/models/models";
import MachineCard from "../../shared/components/MachineryCard";
import RedesignedMachineCard from "./RedesignedMachineCard";
import CompactMachineCard from "./CompactMachineCard";
import CardLayoutToggle, { CardLayout } from "./CardLayoutToggle";
import DecommissionViewToggle from "./DecommissionViewToggle";
import DecommissionDialog from "./DecommissionDialog";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";

// =====================================================================
//  DESIGN TOKENS
// =====================================================================

// =====================================================================
//  FilterItem -- clickable row for sidebar & accordion
// =====================================================================

interface FilterItemProps {
  label: string;
  count: number;
  isSelected: boolean;
  statusColor?: string;
  indent?: boolean;
  dimmed?: boolean;
  onClick: () => void;
}

const FilterItem = ({ label, count, isSelected, statusColor, indent, dimmed, onClick }: FilterItemProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 10px",
      paddingLeft: indent ? "14px" : "10px",
      marginLeft: indent ? "8px" : 0,
      borderRadius: dt.radiusSm,
      cursor: "pointer",
      transition: "all 0.2s ease",
      backgroundColor: isSelected ? (statusColor ? `${statusColor}08` : dt.blueSoft) : "transparent",
      borderLeft: isSelected && statusColor ? `3px solid ${statusColor}` : isSelected ? `3px solid ${dt.blue}` : "3px solid transparent",
      opacity: dimmed && !isSelected ? 0.55 : 1,
      "&:hover": {
        backgroundColor: isSelected ? (statusColor ? `${statusColor}10` : dt.blueGlow) : "#F8FAFC",
        opacity: 1,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        fontFamily: dt.font,
        fontWeight: isSelected ? 600 : 400,
        color: isSelected ? (statusColor || dt.blue) : "#4B5563",
        letterSpacing: "0.1px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
      }}
    >
      {statusColor && (
        <Box
          sx={{
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: statusColor,
            flexShrink: 0,
            opacity: isSelected ? 1 : 0.5,
            transition: "opacity 0.2s",
          }}
        />
      )}
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "11px",
        fontFamily: dt.fontMono,
        fontWeight: 600,
        color: dt.textMuted,
        backgroundColor: dt.inputBg,
        padding: "2px 8px",
        borderRadius: "10px",
        minWidth: "24px",
        textAlign: "center",
        lineHeight: "16px",
        flexShrink: 0,
      }}
    >
      {count}
    </Box>
  </Box>
);

// =====================================================================
//  LOC-4: Location hierarchy type for filter sidebar
// =====================================================================

interface LocationNode {
  id: string;
  name: string;
  parentId?: string;
  parentName?: string;
  count: number;
  children: LocationNode[];
}

// =====================================================================
//  MAIN COMPONENT
// =====================================================================

const Machinery = () => {
  const { query, machines, machine, types, loading, success, error, decommissionedMachines, decommissionedLoading } =
    useAppSelector<CustomerMachineryState>((state: any) => state.customerMachinery);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  // 3-tier responsive: Desktop > 1024, Tablet 768-1024, Phone < 768
  const isCompact = useMediaQuery("(max-width:1024px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [isMachineDialog, setIsMachineDialog] = useState(false);
  const [isQRCodeDialog, setIsQRCodeDialog] = useState(false);
  /* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
  // const [isWhatsAppDialog, setIsWhatsAppDialog] = useState(false);
  // const [selectedMachineForWhatsApp, setSelectedMachineForWhatsApp] = useState<Machine | null>(null);

  // --- Edit dialog (centered MUI Dialog) state ---
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogMachine, setEditDialogMachine] = useState<Machine | null>(null);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(query.search || "");
  const [searchFocused, setSearchFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Group management state ---
  const [groupBy, setGroupBy] = useState<string>(
    () => localStorage.getItem("machinery_groupBy") || "type"
  );
  const [isManageGroupsDialog, setIsManageGroupsDialog] = useState(false);
  const [isAssignGroupDialog, setIsAssignGroupDialog] = useState(false);
  const [selectedMachineForGroup, setSelectedMachineForGroup] = useState<Machine | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // --- Card layout toggle state ---
  const [cardLayout, setCardLayout] = useState<CardLayout>(
    () => (localStorage.getItem("machinery-card-layout") as CardLayout) || "vertical"
  );

  // --- Decommission state ---
  const [viewMode, setViewMode] = useState<"active" | "decommissioned">("active");
  const [decommissionDialogOpen, setDecommissionDialogOpen] = useState(false);
  const [decommissionDialogMachine, setDecommissionDialogMachine] = useState<Machine | null>(null);

  // --- NEW badge state (session-only) ---
  const [newMachineId, setNewMachineId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read ?newMachine= URL param on mount (from catalog page redirect)
  useEffect(() => {
    const newId = searchParams.get("newMachine");
    if (newId) {
      setNewMachineId(newId);
      searchParams.delete("newMachine");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // --- Inject keyframes ---
  useEffect(() => {
    const styleId = "machinery-redesign-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeLeft {
          0% { opacity: 0; transform: translateX(-12px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes headerReveal {
          0% { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes cardReveal {
          0% { opacity: 0; transform: translateY(16px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes newMachinePulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(39, 174, 96, 0);
          }
          50% {
            box-shadow: 0 0 0 4px rgba(39, 174, 96, 0.15), 0 0 8px rgba(39, 174, 96, 0.08);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // --- Load types, groups, and decommissioned count on mount ---
  useEffect(() => {
    dispatch(getTypes());
    dispatch(fetchGroups());
    dispatch(getDecommissionedMachines());
    // Sync persisted groupBy into the query so the first API call uses it
    const savedGroupBy = localStorage.getItem("machinery_groupBy");
    if (savedGroupBy) {
      dispatch(changeQuery({ groupBy: savedGroupBy }));
    }
    return () => {
      dispatch(changeQuery({}));
    };
  }, []);

  // --- Load machines whenever query changes (includes groupBy) ---
  useEffect(() => {
    dispatch(getMachines(query));
  }, [query]);

  // --- Load decommissioned machines on mount and when view changes ---
  useEffect(() => {
    if (viewMode === "decommissioned") {
      dispatch(getDecommissionedMachines());
    }
  }, [viewMode]);



  // =====================================================================
  //  COMPUTED: group & filter decommissioned machines client-side
  // =====================================================================

  /** Group a flat Machine[] into Record<string, Machine[]> using the current groupBy dimension */
  const groupMachinesClientSide = useCallback(
    (flat: Machine[], dimension: string): Record<string, Machine[]> => {
      const result: Record<string, Machine[]> = {};
      for (const m of flat) {
        let keys: string[] = [];
        switch (dimension) {
          case "type":
            keys = [m.type?.name || "Unknown"];
            break;
          case "location":
            // LOC-4: Use authoritative Machine.LocationId instead of MachineGroup
            if (m.locationName) {
              keys = [m.parentLocationName ? `${m.parentLocationName} > ${m.locationName}` : m.locationName];
            } else {
              keys = ["Ungrouped"];
            }
            break;
          case "line":
          case "department":
            keys = m.groupNames && m.groupNames.length > 0 ? m.groupNames : ["Ungrouped"];
            break;
          case "all":
          default:
            keys = ["All Machines"];
            break;
        }
        for (const key of keys) {
          if (!result[key]) result[key] = [];
          result[key].push(m);
        }
      }
      return result;
    },
    []
  );

  /** Filter decommissioned machines client-side using current query (search, typeId, manufacturerId) */
  const filteredDecommissionedMachines = useMemo(() => {
    if (viewMode !== "decommissioned") return [];
    let filtered = [...decommissionedMachines];

    // Filter by search
    if (query.search) {
      const searchLower = query.search.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          (m.model && m.model.toLowerCase().includes(searchLower)) ||
          (m.manufacturer?.name && m.manufacturer.name.toLowerCase().includes(searchLower)) ||
          (m.serialNumber && m.serialNumber.toLowerCase().includes(searchLower)) ||
          (m.customBrandName && m.customBrandName.toLowerCase().includes(searchLower))
      );
    }

    // Filter by type
    if (query.typeId) {
      filtered = filtered.filter((m) => m.type?.id === query.typeId);
    }

    // Filter by manufacturer
    if (query.manufacturerId) {
      filtered = filtered.filter((m) => m.manufacturer?.id === query.manufacturerId);
    }

    return filtered;
  }, [viewMode, decommissionedMachines, query.search, query.typeId, query.manufacturerId]);

  /** Grouped decommissioned machines for the card grid */
  const groupedDecommissionedMachines = useMemo(() => {
    if (viewMode !== "decommissioned") return {};
    return groupMachinesClientSide(filteredDecommissionedMachines, groupBy);
  }, [viewMode, filteredDecommissionedMachines, groupBy, groupMachinesClientSide]);

  // =====================================================================
  //  COMPUTED: unified display data — active or decommissioned
  // =====================================================================

  /** The machines to display in the card grid — either from backend (active) or client-side grouped (decommissioned) */
  const displayMachines = useMemo(() => {
    return viewMode === "decommissioned" ? groupedDecommissionedMachines : (machines || {});
  }, [viewMode, groupedDecommissionedMachines, machines]);

  const isDisplayLoading = viewMode === "decommissioned" ? decommissionedLoading : loading;

  // =====================================================================
  //  COMPUTED: machine counts by type and manufacturer (from displayMachines)
  // =====================================================================

  const totalMachineCount = useMemo(() => {
    if (!displayMachines) return 0;
    return Object.values(displayMachines).reduce((sum, arr) => sum + arr.length, 0);
  }, [displayMachines]);

  /** Total count of active machines only (for the toggle badge) */
  const activeMachineCount = useMemo(() => {
    if (!machines) return 0;
    return Object.values(machines).reduce((sum, arr) => sum + arr.length, 0);
  }, [machines]);

  const typeCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (!displayMachines) return map;
    if (viewMode === "decommissioned") {
      // For decommissioned view, count by type from the flat filtered list
      for (const m of filteredDecommissionedMachines) {
        const typeName = m.type?.name || "Unknown";
        map[typeName] = (map[typeName] || 0) + 1;
      }
    } else {
      for (const [typeName, machineList] of Object.entries(displayMachines)) {
        map[typeName] = machineList.length;
      }
    }
    return map;
  }, [displayMachines, viewMode, filteredDecommissionedMachines]);

  const manufacturerCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (!query.typeId) return map;

    if (viewMode === "decommissioned") {
      // For decommissioned view, count manufacturers from filtered decommissioned machines with matching type
      for (const m of filteredDecommissionedMachines) {
        if (m.type?.id === query.typeId) {
          const mfName = m.manufacturer?.name || "Unknown";
          map[mfName] = (map[mfName] || 0) + 1;
        }
      }
    } else {
      if (!machines) return map;
      const selectedType = types.find((t) => t.id === query.typeId);
      if (!selectedType || !machines[selectedType.name]) return map;
      for (const m of machines[selectedType.name]) {
        const mfName = m.manufacturer?.name || "Unknown";
        map[mfName] = (map[mfName] || 0) + 1;
      }
    }
    return map;
  }, [displayMachines, machines, query.typeId, types, viewMode, filteredDecommissionedMachines]);

  // Derive manufacturer list from user's actual machines (not from catalog API)
  const derivedManufacturers = useMemo(() => {
    if (!query.typeId) return [];

    if (viewMode === "decommissioned") {
      // From decommissioned machines with matching type
      const seen = new Map<string, { id: string; name: string }>();
      for (const m of filteredDecommissionedMachines) {
        if (m.type?.id === query.typeId && m.manufacturer?.id && !seen.has(m.manufacturer.id)) {
          seen.set(m.manufacturer.id, { id: m.manufacturer.id, name: m.manufacturer.name });
        }
      }
      return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
    }

    if (!machines) return [];
    const selectedType = types.find((t) => t.id === query.typeId);
    if (!selectedType || !machines[selectedType.name]) return [];
    const seen = new Map<string, { id: string; name: string }>();
    for (const m of machines[selectedType.name]) {
      if (m.manufacturer?.id && !seen.has(m.manufacturer.id)) {
        seen.set(m.manufacturer.id, { id: m.manufacturer.id, name: m.manufacturer.name });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [machines, query.typeId, types, viewMode, filteredDecommissionedMachines]);

  // =====================================================================
  //  COMPUTED: location hierarchy for filter sidebar (LOC-4)
  // =====================================================================

  /** Derive location hierarchy from all displayed machines */
  const locationHierarchy = useMemo(() => {
    const allMachines: Machine[] = displayMachines
      ? Object.values(displayMachines).flat()
      : [];

    // Build a map: parentName -> { children: { childName -> count } }
    const parentMap = new Map<string, { count: number; children: Map<string, number> }>();

    for (const m of allMachines) {
      if (!m.locationName) continue;
      if (m.parentLocationName) {
        // Has parent
        if (!parentMap.has(m.parentLocationName)) {
          parentMap.set(m.parentLocationName, { count: 0, children: new Map() });
        }
        const parent = parentMap.get(m.parentLocationName)!;
        parent.count += 1;
        parent.children.set(m.locationName, (parent.children.get(m.locationName) || 0) + 1);
      } else {
        // Top-level location (no parent)
        if (!parentMap.has(m.locationName)) {
          parentMap.set(m.locationName, { count: 0, children: new Map() });
        }
        parentMap.get(m.locationName)!.count += 1;
      }
    }

    // Convert to sorted array
    const result: LocationNode[] = [];
    for (const [parentName, data] of Array.from(parentMap.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
      const children: LocationNode[] = Array.from(data.children.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, count]) => ({
          id: `${parentName}>${name}`,
          name,
          parentName,
          count,
          children: [],
        }));
      // Parent count: if it has children, sum children counts; otherwise use own count
      const totalCount = children.length > 0
        ? children.reduce((s, c) => s + c.count, 0)
        : data.count;
      result.push({
        id: parentName,
        name: parentName,
        count: totalCount,
        children,
      });
    }

    return result;
  }, [displayMachines]);

  // Location filter state (visual scaffold — Machinery Dev wires actual filtering)
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());

  const handleLocationToggle = useCallback((locationId: string, isParent: boolean, children?: LocationNode[]) => {
    setSelectedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
        // If parent, uncheck all children
        if (isParent && children) {
          for (const c of children) next.delete(c.id);
        }
      } else {
        next.add(locationId);
        // If parent, check all children
        if (isParent && children) {
          for (const c of children) next.add(c.id);
        }
      }
      // If all children of a parent are checked, check the parent too
      if (!isParent && children) {
        // Find parent — this callback doesn't know the parent ID directly
        // Handled below in the render via parent auto-check
      }
      return next;
    });
  }, []);

  // =====================================================================
  //  EVENT HANDLERS
  // =====================================================================

  const handleAdd = () => {
    dispatch(selectMachine(null));
    setIsMachineDialog(true);
  };

  const handleQRCode = (machine: Machine) => {
    if (!machine) return;
    dispatch(selectMachine(machine));
    setIsQRCodeDialog(true);
  };

  /* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
  // const handleWhatsAppBot = (machine: Machine) => {
  //   if (!machine) return;
  //   setSelectedMachineForWhatsApp(machine);
  //   setIsWhatsAppDialog(true);
  // };

  // const handleCloseWhatsAppDialog = () => {
  //   setIsWhatsAppDialog(false);
  //   setSelectedMachineForWhatsApp(null);
  // };

  const handleEdit = (machine: Machine) => {
    dispatch(selectMachine(machine));
    setIsMachineDialog(true);
  };

  const handleEditDetails = (machine: Machine) => {
    setEditDialogMachine(machine);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditDialogMachine(null);
  };

  const handleEditDialogSaved = () => {
    setEditDialogOpen(false);
    setEditDialogMachine(null);
  };

  const handleCreateServiceRequest = (machine: Machine) => {
    if (!machine) return;
    navigate(`/service-requests/new?machine=${machine.id}`);
  };

  const handleCreateServiceReport = (machine: Machine) => {
    if (!machine) return;
    navigate(`/machinery/${machine.id}/service-reports/new`);
  };

  const handleDuplicate = async (machine: Machine) => {
    if (!machine) return;
    const model: ChangeMachine = {
      model: machine.model,
      serialNumber: "",
      typeId: machine.type?.id,
      manufacturerId: machine.manufacturer?.id,
      customBrandName: machine.customBrandName,
      operationTypeId: machine.operationType?.id,
      axisConfigurationId: machine.axisConfiguration?.id,
      controlSystemBrandId: machine.controlSystemBrand?.id,
      automationLevelId: machine.automationLevel?.id,
      system: machine.system,
      version: machine.version,
      productionYear: machine.productionYear,
      description: machine.description,
      numberOfStations: machine.numberOfStations,
      image: machine.image,
    };
    if (!model.typeId || !model.manufacturerId || !model.operationTypeId || !model.controlSystemBrandId) {
      console.error("Missing required fields for machine duplication");
      return;
    }
    await dispatch(createMachine(model));
    await dispatch(getMachines(query));
  };

  const handleDecommission = (machine: Machine) => {
    setDecommissionDialogMachine(machine);
    setDecommissionDialogOpen(true);
  };

  const handleDecommissioned = async () => {
    setDecommissionDialogOpen(false);
    setDecommissionDialogMachine(null);
    await dispatch(getMachines(query));
    await dispatch(getDecommissionedMachines());
  };

  const handleRestore = async (machine: Machine) => {
    if (!machine) return;
    confirm({
      title: translate("restoreMachine"),
      text: translate("restoreConfirm"),
      confirmButton: translate("restore"),
      closeButton: tCommon("cancel"),
      severity: "neutral",
      onConfirm: async () => {
        try {
          await dispatch(restoreMachine(machine.id)).unwrap();
          dispatch(setSuccess(translate("restored")));
          await dispatch(getMachines(query));
          await dispatch(getDecommissionedMachines());
        } catch (err) {
          // Error handled by slice
        }
      },
    });
  };

  // --- NEW badge: callback when machine is created from dialog on this page ---
  const handleMachineCreated = useCallback((newId?: string) => {
    if (newId) {
      setNewMachineId(newId);
      // Scroll the new card into view after the list re-renders
      setTimeout(() => {
        const el = document.querySelector(`[data-machine-id="${newId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 600);
    }
  }, []);

  const handleQueryChange = useCallback(
    (value: MachinesQuery) => {
      dispatch(changeQuery({ ...value }));
    },
    [dispatch]
  );

  // --- Search with debounce ---
  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        handleQueryChange({ ...query, search: undefined });
        return;
      }
      debounceRef.current = setTimeout(() => {
        handleQueryChange({ ...query, search: value || undefined });
      }, 400);
    },
    [query, handleQueryChange]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        handleQueryChange({ ...query, search: searchValue || undefined });
      }
    },
    [searchValue, query, handleQueryChange]
  );

  // --- Type filter ---
  const handleTypeFilter = useCallback(
    (typeId: string | undefined) => {
      handleQueryChange({ ...query, typeId, manufacturerId: typeId ? query.manufacturerId : undefined });
    },
    [query, handleQueryChange]
  );

  // --- Manufacturer filter ---
  const handleManufacturerFilter = useCallback(
    (manufacturerId: string | undefined) => {
      handleQueryChange({ ...query, manufacturerId });
    },
    [query, handleQueryChange]
  );

  // --- Clear all filters ---
  const handleClearFilters = useCallback(() => {
    setSearchValue("");
    handleQueryChange({});
  }, [handleQueryChange]);

  // --- Group-by dimension change ---
  const handleGroupByChange = useCallback(
    (newGroupBy: string) => {
      setGroupBy(newGroupBy);
      setCollapsedGroups(new Set());
      localStorage.setItem("machinery_groupBy", newGroupBy);
      handleQueryChange({ ...query, groupBy: newGroupBy });
    },
    [query, handleQueryChange]
  );

  // --- Assign group dialog ---
  const handleAssignGroup = useCallback((m: Machine) => {
    setSelectedMachineForGroup(m);
    setIsAssignGroupDialog(true);
  }, []);

  const handleAssignGroupClose = useCallback(() => {
    setIsAssignGroupDialog(false);
    setSelectedMachineForGroup(null);
  }, []);

  const handleGroupAssigned = useCallback(() => {
    dispatch(getMachines(query));
    dispatch(fetchGroups());
  }, [dispatch, query]);

  // --- Toggle group collapse ---
  const handleToggleGroupCollapse = useCallback((groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);


  // =====================================================================
  //  Active filter count for accordion badge
  // =====================================================================

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (query.typeId) count++;
    if (query.manufacturerId) count++;
    if (query.search) count++;
    return count;
  }, [query]);

  // =====================================================================
  //  SHARED: renderFilterContent (sidebar & accordion)
  // =====================================================================

  const renderFilterContent = () => (
    <>
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
          padding: "0 12px",
          height: "40px",
          transition: "all 0.25s ease",
          boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
          mb: "16px",
        }}
      >
        <SearchOutlined
          sx={{ fontSize: "16px", color: searchFocused ? dt.blue : dt.textMuted, flexShrink: 0, transition: "color 0.2s" }}
        />
        <input
          type="text"
          value={searchValue}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder={translate("searchByMachineModel") + "..."}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: "13px",
            fontFamily: 'var(--font-sans)',
            color: dt.textPrimary,
            background: "transparent",
            marginLeft: "8px",
          }}
        />
      </Box>

      {/* Filter header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px", px: "4px" }}>
        <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font }}>
          {translate("machineType")}
        </Box>
        {activeFilterCount > 0 && (
          <Box
            onClick={handleClearFilters}
            sx={{ fontSize: "12px", color: dt.blue, cursor: "pointer", fontWeight: 500, fontFamily: dt.font, "&:hover": { textDecoration: "underline" } }}
          >
            {translate("clearAll")}
          </Box>
        )}
      </Box>

      {/* "All" filter item */}
      <FilterItem
        label={tCommon("all")}
        count={totalMachineCount}
        isSelected={!query.typeId}
        onClick={() => handleTypeFilter(undefined)}
      />

      {/* Individual type filters */}
      {types.map((type) => (
        <FilterItem
          key={type.id}
          label={type.name}
          count={typeCountMap[type.name] || 0}
          isSelected={query.typeId === type.id}
          statusColor={dt.blue}
          indent
          onClick={() => handleTypeFilter(type.id)}
        />
      ))}

      {/* Manufacturer filter group (only shown when a type is selected) */}
      {query.typeId && derivedManufacturers.length > 0 && (
        <>
          {/* Separator */}
          <Box sx={{ borderTop: `1px dashed ${dt.cardBorder}`, my: "12px", mx: "4px" }} />

          <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font, mb: "10px", px: "4px" }}>
            {tCommon("brand")}
          </Box>

          {/* "All brands" */}
          <FilterItem
            label={tCommon("all")}
            count={
              types.find((t) => t.id === query.typeId)
                ? (typeCountMap[types.find((t) => t.id === query.typeId)!.name] || 0)
                : 0
            }
            isSelected={!query.manufacturerId}
            onClick={() => handleManufacturerFilter(undefined)}
          />

          {/* Individual manufacturer filters */}
          {derivedManufacturers.map((mf) => (
            <FilterItem
              key={mf.id}
              label={mf.name}
              count={manufacturerCountMap[mf.name] || 0}
              isSelected={query.manufacturerId === mf.id}
              statusColor="#8E44AD"
              indent
              onClick={() => handleManufacturerFilter(mf.id)}
            />
          ))}
        </>
      )}

      {/* Location filter section (LOC-4 — visual scaffold, Machinery Dev wires actual filtering) */}
      {locationHierarchy.length > 0 && (
        <>
          <Box sx={{ borderTop: `1px dashed ${dt.cardBorder}`, my: "12px", mx: "4px" }} />

          <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font, mb: "10px", px: "4px" }}>
            {translate("locationFilter")}
          </Box>

          {locationHierarchy.map((parent) => {
            const parentChecked = selectedLocations.has(parent.id);
            const someChildrenChecked = parent.children.length > 0 && parent.children.some((c) => selectedLocations.has(c.id));
            const allChildrenChecked = parent.children.length > 0 && parent.children.every((c) => selectedLocations.has(c.id));

            return (
              <React.Fragment key={parent.id}>
                {/* Parent location */}
                <Box
                  onClick={() => handleLocationToggle(parent.id, true, parent.children)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    borderRadius: dt.radiusSm,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: parentChecked || allChildrenChecked ? dt.blueSoft : "transparent",
                    "&:hover": { backgroundColor: parentChecked || allChildrenChecked ? dt.blueGlow : "#F8FAFC" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, flex: 1 }}>
                    <Checkbox
                      size="small"
                      checked={parentChecked || allChildrenChecked}
                      indeterminate={someChildrenChecked && !allChildrenChecked && !parentChecked}
                      sx={{
                        padding: 0,
                        color: dt.cardBorder,
                        "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: dt.blue },
                        "& .MuiSvgIcon-root": { fontSize: "18px" },
                      }}
                    />
                    <PlaceOutlined sx={{ fontSize: "14px", color: dt.textMuted, flexShrink: 0 }} />
                    <Box sx={{
                      fontSize: "13px", fontFamily: dt.font, fontWeight: 600, color: dt.textPrimary,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                    }}>
                      {parent.name}
                    </Box>
                  </Box>
                  <Box sx={{
                    fontSize: "11px", fontFamily: dt.fontMono, fontWeight: 600, color: dt.textMuted,
                    backgroundColor: dt.inputBg, padding: "2px 8px", borderRadius: "10px",
                    minWidth: "24px", textAlign: "center", lineHeight: "16px", flexShrink: 0,
                  }}>
                    {parent.count}
                  </Box>
                </Box>

                {/* Child locations */}
                {parent.children.map((child) => (
                  <Box
                    key={child.id}
                    onClick={() => handleLocationToggle(child.id, false)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "5px 10px",
                      paddingLeft: "32px",
                      borderRadius: dt.radiusSm,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: selectedLocations.has(child.id) ? dt.blueSoft : "transparent",
                      "&:hover": { backgroundColor: selectedLocations.has(child.id) ? dt.blueGlow : "#F8FAFC" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, flex: 1 }}>
                      <Checkbox
                        size="small"
                        checked={selectedLocations.has(child.id)}
                        sx={{
                          padding: 0,
                          color: dt.cardBorder,
                          "&.Mui-checked": { color: dt.blue },
                          "& .MuiSvgIcon-root": { fontSize: "18px" },
                        }}
                      />
                      <Box sx={{
                        fontSize: "13px", fontFamily: dt.font, fontWeight: 400, color: "#4B5563",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                      }}>
                        {child.name}
                      </Box>
                    </Box>
                    <Box sx={{
                      fontSize: "11px", fontFamily: dt.fontMono, fontWeight: 600, color: dt.textMuted,
                      backgroundColor: dt.inputBg, padding: "2px 8px", borderRadius: "10px",
                      minWidth: "24px", textAlign: "center", lineHeight: "16px", flexShrink: 0,
                    }}>
                      {child.count}
                    </Box>
                  </Box>
                ))}
              </React.Fragment>
            );
          })}
        </>
      )}
    </>
  );

  // =====================================================================
  //  SHARED: renderCardGrid (machine cards grouped by dimension)
  // =====================================================================

  // Group accent colors for section headers (left border bar)
  const GROUP_ACCENT_COLORS = [
    "#3498DB", "#2ECC71", "#E74C3C", "#F39C12", "#9B59B6",
    "#1ABC9C", "#E91E63", "#607D8B", "#FF5722", "#795548",
  ];

  const renderCardGrid = () => {
    if (!displayMachines || !Object.keys(displayMachines).length || totalMachineCount === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: dt.cardBg,
            borderRadius: dt.radiusLg,
            border: `1px solid ${dt.cardBorder}`,
            boxShadow: dt.cardShadow,
            minHeight: "400px",
            flex: 1,
          }}
        >
          <TableEmpty
            iconComponent={viewMode === "decommissioned" ? () => <DeleteSweepOutlined sx={{ fontSize: "48px", color: dt.textMuted }} /> : BoxSearchIcon}
            text={viewMode === "decommissioned" ? translate("noDecommissionedMachines") : translate("noMachinesFound")}
            secondaryText={viewMode === "decommissioned" ? translate("noDecommissionedMachinesDescription") : translate("noMachinesFoundSecondary")}
          />
        </Box>
      );
    }

    let cardIndex = 0;
    const groupEntries = Object.entries(displayMachines);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", flex: 1 }}>
        {groupEntries.map(([groupName, groupMachines], groupIdx) => {
          if (!groupMachines || groupMachines.length === 0) return null;
          const isCollapsed = collapsedGroups.has(groupName);
          const accentColor = GROUP_ACCENT_COLORS[groupIdx % GROUP_ACCENT_COLORS.length];

          return (
            <Box key={groupName}>
              {/* Group section header -- collapsible with left accent bar */}
              <Box
                onClick={() => handleToggleGroupCollapse(groupName)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  mb: isCollapsed ? 0 : "16px",
                  cursor: "pointer",
                  userSelect: "none",
                  padding: "8px 12px",
                  borderRadius: dt.radiusSm,
                  borderLeft: `4px solid ${accentColor}`,
                  backgroundColor: dt.headerBg,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: dt.rowHover,
                  },
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    fontSize: "20px",
                    color: dt.textSecondary,
                    transition: "transform 0.2s ease",
                    transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                  }}
                />
                <Box
                  sx={{
                    fontSize: { xs: "15px", md: "17px" },
                    fontWeight: 700,
                    fontFamily: dt.font,
                    color: dt.textPrimary,
                    letterSpacing: "-0.2px",
                    flex: 1,
                  }}
                >
                  {groupName}
                </Box>
                <Box
                  sx={{
                    fontSize: "12px",
                    fontFamily: dt.fontMono,
                    fontWeight: 700,
                    color: accentColor,
                    backgroundColor: `${accentColor}10`,
                    border: `1px solid ${accentColor}30`,
                    borderRadius: "12px",
                    padding: "2px 10px",
                    lineHeight: "18px",
                  }}
                >
                  {groupMachines.length}
                </Box>
              </Box>

              {/* Card grid -- hidden when collapsed */}
              {!isCollapsed && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: cardLayout === "compact"
                      ? { xs: "1fr", sm: "repeat(auto-fill, minmax(280px, 1fr))" }
                      : cardLayout === "horizontal"
                      ? { xs: "1fr", lg: "repeat(auto-fill, minmax(480px, 1fr))" }
                      : { xs: "1fr", sm: "repeat(auto-fill, minmax(280px, 1fr))" },
                    gap: cardLayout === "compact" ? "8px" : "16px",
                  }}
                >
                  {groupMachines.map((m) => {
                    const idx = cardIndex++;
                    return (
                      <Box
                        key={m.id}
                        data-machine-id={m.id}
                        sx={{
                          animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${Math.min(idx * 0.06, 0.6)}s both`,
                        }}
                      >
                        {cardLayout === "compact" ? (
                          <CompactMachineCard
                            machine={m}
                            showActions={true}
                            isNew={m.id === newMachineId}
                            onQRCode={handleQRCode}
                            /* WhatsApp sunset 2026-03-14 */ // onWhatsAppBot={handleWhatsAppBot}
                            onDecommission={handleDecommission}
                            onRestore={handleRestore}
                            onEdit={handleEdit}
                            onEditDetails={handleEditDetails}
                            onCreateServiceRequest={handleCreateServiceRequest}
                            onCreateServiceReport={handleCreateServiceReport}
                            onDuplicate={handleDuplicate}
                            onAssignGroup={handleAssignGroup}
                          />
                        ) : cardLayout === "horizontal" ? (
                          <RedesignedMachineCard
                            machine={m}
                            showActions={true}
                            isNew={m.id === newMachineId}
                            groupNames={m.groupNames}
                            onQRCode={handleQRCode}
                            /* WhatsApp sunset 2026-03-14 */ // onWhatsAppBot={handleWhatsAppBot}
                            onDecommission={handleDecommission}
                            onRestore={handleRestore}
                            onEdit={handleEdit}
                            onEditDetails={handleEditDetails}
                            onCreateServiceRequest={handleCreateServiceRequest}
                            onCreateServiceReport={handleCreateServiceReport}
                            onDuplicate={handleDuplicate}
                            onAssignGroup={handleAssignGroup}
                          />
                        ) : (
                          <MachineCard
                            machine={m}
                            showActions={true}
                            isNew={m.id === newMachineId}
                            hideServiceBadge
                            onQRCode={handleQRCode}
                            /* WhatsApp sunset 2026-03-14 */ // onWhatsAppBot={handleWhatsAppBot}
                            onDecommission={handleDecommission}
                            onRestore={handleRestore}
                            onEdit={handleEdit}
                            onEditDetails={handleEditDetails}
                            onCreateServiceRequest={handleCreateServiceRequest}
                            onCreateServiceReport={handleCreateServiceReport}
                            onDuplicate={handleDuplicate}
                            onAssignGroup={handleAssignGroup}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // =====================================================================
  //  RENDER
  // =====================================================================


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflow: "hidden",
        px: { xs: "16px", md: "24px" },
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      {/* Add Machine button -- above the sidebar+content row, Write+ only, hidden in decommissioned view */}
      {viewMode === "active" && <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: { xs: 2, md: 3 },
            animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) both",
          }}
        >
              <Button
                variant="contained"
                onClick={handleAdd}
                startIcon={<AddOutlined sx={{ fontSize: isPhone ? "16px !important" : "18px !important" }} />}
                sx={{
                  background: accentGradientDark,
                  borderRadius: dt.radiusPill,
                  padding: isPhone ? "7px 16px" : { xs: "8px 18px", md: "11px 30px" },
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: isPhone ? "12px" : { xs: "13px", md: "14px" },
                  fontFamily: dt.font,
                  boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minWidth: isPhone ? "auto" : undefined,
                  color: "#FFFFFF",
                  "&:hover": {
                    background: accentGradientDarkHover,
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {isPhone ? translate("addMachine") : translate("addNewMachine")}
              </Button>
        </Box>
      </PermissionGuard>}

      {/* =====================================================
          MAIN CONTENT -- sidebar + content row
          ===================================================== */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* Desktop sidebar -- paddingTop aligns with first card (below group header) */}
        {!isCompact && (
          <Box
            sx={{
              width: "260px",
              minWidth: "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              paddingTop: "40px",
              overflowY: "auto",
              animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: "22px",
                boxShadow: dt.cardShadow,
              }}
            >
              {renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* Main area -- accordion + cards (SCROLLS HERE) */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
            overflowY: "auto",
            pb: 3,
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
            /* Styled scrollbar */
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.15)",
              borderRadius: "3px",
              "&:hover": { background: "rgba(0,0,0,0.25)" },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0,0,0,0.15) transparent",
          }}
        >
          {/* View toggle + Group toggle pills + Manage Groups button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <DecommissionViewToggle
                activeView={viewMode}
                activeCount={activeMachineCount}
                decommissionedCount={decommissionedMachines.length}
                onViewChange={setViewMode}
              />
              <GroupTogglePills
                activeGroupBy={groupBy}
                onGroupByChange={handleGroupByChange}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <Button
                  size="small"
                  startIcon={<SettingsIcon sx={{ fontSize: "16px !important" }} />}
                  onClick={() => setIsManageGroupsDialog(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    fontFamily: dt.font,
                    color: dt.textSecondary,
                    borderRadius: dt.radiusSm,
                    border: `1px solid ${dt.cardBorder}`,
                    padding: "5px 14px",
                    "&:hover": {
                      borderColor: dt.blue,
                      color: dt.blue,
                      backgroundColor: dt.blueSoft,
                    },
                  }}
                >
                  {!isPhone && translate("manageGroups")}
                </Button>
              </PermissionGuard>
            </Box>
          </Box>

          {/* Accordion filters (tablet & phone) */}
          {isCompact && (
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded(!filtersExpanded)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: dt.textSecondary }} />}
                sx={{
                  minHeight: "48px",
                  px: 2,
                  "&.Mui-expanded": { minHeight: "48px" },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 1,
                    my: "10px",
                    "&.Mui-expanded": { my: "10px" },
                  },
                }}
              >
                <FilterListIcon sx={{ color: dt.textMuted, fontSize: "20px" }} />
                <Box sx={{ fontSize: "14px", fontWeight: 500, color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("filters")}
                </Box>
                {activeFilterCount > 0 && (
                  <Badge
                    badgeContent={activeFilterCount}
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        backgroundColor: dt.blue,
                        color: "#fff",
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {renderFilterContent()}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Card layout toggle */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <CardLayoutToggle layout={cardLayout} onLayoutChange={setCardLayout} />
          </Box>

          {/* Loading / Card grid — unified for both active and decommissioned views */}
          {isDisplayLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <Loader />
            </Box>
          ) : (
            renderCardGrid()
          )}

        </Box>
      </Box>

      {/* =====================================================
          DIALOGS (unchanged)
          ===================================================== */}
      {isMachineDialog && (
        <MachineDialog
          key={`machine-dialog-${isMachineDialog}`}
          open={isMachineDialog}
          onClose={() => setIsMachineDialog(false)}
          machine={machine}
          isNew={!machine}
          onCreated={handleMachineCreated}
        />
      )}

      {!!machine && (
        <MachineQRCodeDialog
          key={`qr-code-dialog-${isQRCodeDialog}`}
          open={isQRCodeDialog}
          onClose={() => setIsQRCodeDialog(false)}
          machine={machine}
        />
      )}

      {/* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */}
      {/* {!!selectedMachineForWhatsApp && (
        <MachineWhatsAppQRDialog
          key={`whatsapp-qr-dialog-${isWhatsAppDialog}`}
          open={isWhatsAppDialog}
          onClose={handleCloseWhatsAppDialog}
          machine={selectedMachineForWhatsApp}
        />
      )} */}

      <ManageGroupsDialog
        open={isManageGroupsDialog}
        onClose={() => setIsManageGroupsDialog(false)}
      />

      <AssignGroupDialog
        open={isAssignGroupDialog}
        onClose={handleAssignGroupClose}
        machine={selectedMachineForGroup}
        onAssigned={handleGroupAssigned}
      />

      {/* Edit dialog (centered MUI Dialog) */}
      {editDialogOpen && editDialogMachine && (
        <MachineEditDialog
          open={editDialogOpen}
          machine={editDialogMachine}
          onClose={handleEditDialogClose}
          onSaved={handleEditDialogSaved}
        />
      )}

      {/* Decommission dialog */}
      <DecommissionDialog
        open={decommissionDialogOpen}
        machine={decommissionDialogMachine}
        onClose={() => {
          setDecommissionDialogOpen(false);
          setDecommissionDialogMachine(null);
        }}
        onDecommissioned={handleDecommissioned}
      />
    </Box>
  );
};

export default Machinery;
